/**
 * DOM builder functions for panel UI
 * Creates DOM elements instead of HTML strings
 */

import { highlightSearchTerm } from '../highlightUtils.js';
import { renderRoleReferences } from '../../shared/roleReferences.js';
import { showMyRolesActive } from '../state.js';
import { flattenRoles } from './utils.js';
import { t } from '../utils/i18n.js';

/**
 * Predefined role size options
 * Ensures minimum value of 10 for parent circle visibility
 */
export const VALUE_OPTIONS = [
    { label: 'Size 1 – value 10', value: 10 },
    { label: 'Size 2 – value 50', value: 50 },
    { label: 'Size 3 – value 100', value: 100 },
    { label: 'Size 4 – value 150', value: 150 },
    { label: 'Size 5 – value 200', value: 200 }
];

/**
 * Build a select dropdown for role value/size selection
 * @param id - Element ID for the select
 * @param selectedValue - Current value to preselect
 * @param nodeName - Optional node name for data attribute
 * @param field - Optional field name for data attribute
 * @returns HTMLSelectElement with preset size options
 */
function buildValueSelect(id: string, selectedValue: number, nodeName?: string, field?: string): HTMLSelectElement {
    const select = document.createElement('select');
    select.id = id;
    select.setAttribute('aria-label', 'Role size preset');
    if (nodeName) select.dataset.node = nodeName;
    if (field) select.dataset.field = field;

    VALUE_OPTIONS.forEach(optionData => {
        const option = document.createElement('option');
        option.value = String(optionData.value);
        option.textContent = optionData.label;
        select.appendChild(option);
    });

    // Add custom option if current value doesn't match presets
    if (selectedValue !== undefined && !VALUE_OPTIONS.some(opt => opt.value === selectedValue)) {
        const customOption = document.createElement('option');
        customOption.value = String(selectedValue);
        customOption.textContent = `Current (value ${selectedValue})`;
        select.appendChild(customOption);
    }

    select.value = String(selectedValue ?? 100);
    return select;
}

/**
 * Build value field section with size dropdown (for edit mode)
 * @param nodeName - Role name for data attribute
 * @param value - Current role value
 * @returns HTMLElement containing value field with dropdown and hint
 */
function buildValueField(nodeName: string, value: number): HTMLElement {
    const valueDiv = document.createElement('div');
    valueDiv.className = 'value';

    const labelDiv = document.createElement('div');
    labelDiv.className = 'label';
    labelDiv.textContent = t('panel.details.value');

    const editableFieldDiv = document.createElement('div');
    editableFieldDiv.className = 'editable-field editable-field--select';

    const select = buildValueSelect('edit-value', value, nodeName, 'value');

    const saveHint = document.createElement('span');
    saveHint.className = 'save-hint';
    saveHint.textContent = t('panel.edit.saveOnChange');

    editableFieldDiv.appendChild(select);
    editableFieldDiv.appendChild(saveHint);
    valueDiv.appendChild(labelDiv);
    valueDiv.appendChild(editableFieldDiv);

    return valueDiv;
}

/**
 * Build add child form
 */
export function buildAddChildForm(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'add-child-form';
    
    const nameGroup = document.createElement('div');
    nameGroup.className = 'form-group';
    const nameLabel = document.createElement('label');
    nameLabel.textContent = t('panel.create.childName');
    nameLabel.setAttribute('for', 'child-name');
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.id = 'child-name';
    nameInput.placeholder = 'Enter role name';
    nameInput.setAttribute('aria-label', 'Child role name');
    nameInput.setAttribute('aria-required', 'true');
    nameGroup.appendChild(nameLabel);
    nameGroup.appendChild(nameInput);
    
    const valueGroup = document.createElement('div');
    valueGroup.className = 'form-group';
    const valueLabel = document.createElement('label');
    valueLabel.textContent = t('panel.create.size');
    const valueSelect = buildValueSelect('child-value', 100);
    valueGroup.appendChild(valueLabel);
    valueGroup.appendChild(valueSelect);
    
    const colorGroup = document.createElement('div');
    colorGroup.className = 'form-group';
    const colorLabel = document.createElement('label');
    colorLabel.textContent = t('panel.create.color');
    colorLabel.setAttribute('for', 'child-color');
    const colorInput = document.createElement('input');
    colorInput.type = 'text';
    colorInput.id = 'child-color';
    colorInput.placeholder = '#ffffff';
    colorInput.value = '#ffffff';
    colorInput.setAttribute('aria-label', 'Role color (hex code)');
    colorGroup.appendChild(colorLabel);
    colorGroup.appendChild(colorInput);
    
    const createBtn = document.createElement('button');
    createBtn.id = 'create-btn';
    createBtn.className = 'create-btn';
    createBtn.textContent = t('panel.create.createButton');
    
    const cancelBtn = document.createElement('button');
    cancelBtn.id = 'cancel-btn';
    cancelBtn.className = 'cancel-btn';
    cancelBtn.textContent = t('common.cancel');
    
    container.appendChild(nameGroup);
    container.appendChild(valueGroup);
    container.appendChild(colorGroup);
    container.appendChild(createBtn);
    container.appendChild(cancelBtn);
    
    return container;
}

/**
 * Build placeholder message
 */
export function buildPlaceholderMessage(): HTMLElement {
    const p = document.createElement('p');
    p.className = 'panel-message panel-message--placeholder';
    p.textContent = t('panel.placeholder');
    return p;
}

/**
 * Build success message
 */
export function buildSuccessMessage(message: string): HTMLElement {
    const p = document.createElement('p');
    p.className = 'panel-message panel-message--success';
    p.textContent = message;
    return p;
}

/**
 * Build editable field
 */
export function buildEditableField(label: string, value: string | number, nodeName: string, field: string): HTMLElement {
    const valueDiv = document.createElement('div');
    valueDiv.className = 'value';
    
    const labelDiv = document.createElement('div');
    labelDiv.className = 'label';
    labelDiv.textContent = label;
    
    const editableFieldDiv = document.createElement('div');
    editableFieldDiv.className = 'editable-field';
    
    const input = document.createElement('input');
    input.type = 'text';
    input.id = `edit-${field}`;
    input.value = String(value);
    input.dataset.node = nodeName;
    input.dataset.field = field;
    
    const saveHint = document.createElement('span');
    saveHint.className = 'save-hint';
    saveHint.textContent = t('panel.edit.enterToSave');
    
    editableFieldDiv.appendChild(input);
    editableFieldDiv.appendChild(saveHint);
    valueDiv.appendChild(labelDiv);
    valueDiv.appendChild(editableFieldDiv);
    
    return valueDiv;
}

/**
 * Build back button
 */
export function buildBackButton(): HTMLElement | null {
    if (!showMyRolesActive) return null;
    
    const button = document.createElement('button');
    button.className = 'role-panel__back-to-my-roles-btn';
    button.title = 'Back to my roles overview';
    button.textContent = t('panel.actions.myRoles');
    return button;
}

/**
 * Build view mode link
 */
export function buildViewModeLink(): HTMLElement {
    const button = document.createElement('button');
    button.className = 'role-panel__back-to-my-roles-btn';
    button.id = 'view-mode-link';
    button.textContent = t('panel.actions.viewMode');
    return button;
}

/**
 * Build role details section (readonly view)
 */
export function buildRoleDetailsView(node: any, roleMap?: Map<number, string>): HTMLElement {
    const container = document.createElement('div');
    
    const backBtn = buildBackButton();
    if (backBtn) container.appendChild(backBtn);
    
    // Role details
    const detailsDiv = document.createElement('div');
    detailsDiv.className = 'role-details';
    
    container.appendChild(detailsDiv);

    // Action icons in panel header (edit, add child, delete)
    const panelHeader = document.querySelector('.role-panel__header');
    if (panelHeader) {
        // Remove existing action icons if present
        const existingEditLink = panelHeader.querySelector('#edit-mode-link');
        if (existingEditLink) {
            existingEditLink.remove();
        }
        const existingAddChildBtn = panelHeader.querySelector('#add-child-icon-btn');
        if (existingAddChildBtn) {
            existingAddChildBtn.remove();
        }
        const existingDeleteBtn = panelHeader.querySelector('#delete-node-icon-btn');
        if (existingDeleteBtn) {
            existingDeleteBtn.remove();
        }
        
        // Edit icon
        const editLink = document.createElement('a');
        editLink.href = '#';
        editLink.id = 'edit-mode-link';
        editLink.className = 'edit-mode-link';
        editLink.setAttribute('aria-label', 'Edit role');
        editLink.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
        </svg>`;
        panelHeader.appendChild(editLink);
        
        // Add child icon button
        const addChildBtn = document.createElement('button');
        addChildBtn.id = 'add-child-icon-btn';
        addChildBtn.className = 'action-icon-btn add-child-icon-btn';
        addChildBtn.setAttribute('aria-label', 'Add child role');
        addChildBtn.setAttribute('title', 'Add child role');
        addChildBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1a.5.5 0 0 1 .5.5v6h6a.5.5 0 0 1 0 1h-6v6a.5.5 0 0 1-1 0v-6h-6a.5.5 0 0 1 0-1h6v-6A.5.5 0 0 1 8 1z"/>
        </svg>`;
        panelHeader.appendChild(addChildBtn);
        
        // Delete icon button
        const deleteBtn = document.createElement('button');
        deleteBtn.id = 'delete-node-icon-btn';
        deleteBtn.className = 'action-icon-btn delete-node-icon-btn';
        deleteBtn.setAttribute('aria-label', 'Delete role');
        deleteBtn.setAttribute('title', 'Delete role');
        const childrenCount = node.children ? node.children.length : 0;
        if (childrenCount > 0) {
            deleteBtn.disabled = true;
            deleteBtn.setAttribute('aria-disabled', 'true');
            deleteBtn.setAttribute('title', 'Cannot delete role with children');
            deleteBtn.className += ' disabled';
        }
        deleteBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>`;
        panelHeader.appendChild(deleteBtn);
    }
    
    // Create tabbed interface for Details, People, Contained Roles, and References
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'panel-tabs-container';
    
    // Tab buttons
    const tabButtons = document.createElement('div');
    tabButtons.className = 'role-panel__tab-buttons';
    
    const detailsTabBtn = document.createElement('button');
    detailsTabBtn.className = 'role-panel__tab-btn role-panel__tab-btn--active';
    detailsTabBtn.dataset.tab = 'details';
    detailsTabBtn.textContent = t('panel.tabs.details');
    detailsTabBtn.setAttribute('aria-label', 'Role tab');
    
    const peopleTabBtn = document.createElement('button');
    peopleTabBtn.className = 'role-panel__tab-btn';
    peopleTabBtn.dataset.tab = 'people';
    peopleTabBtn.textContent = t('panel.tabs.people');
    peopleTabBtn.setAttribute('aria-label', 'People in this role tab');
    
    const containedTabBtn = document.createElement('button');
    containedTabBtn.className = 'role-panel__tab-btn';
    containedTabBtn.dataset.tab = 'contained';
    containedTabBtn.textContent = t('panel.tabs.contained');
    containedTabBtn.setAttribute('aria-label', 'Contained roles tab');
    
    const referencedTabBtn = document.createElement('button');
    referencedTabBtn.className = 'role-panel__tab-btn';
    referencedTabBtn.dataset.tab = 'referenced';
    referencedTabBtn.textContent = t('panel.tabs.referenced');
    referencedTabBtn.setAttribute('aria-label', 'Referenced by tab');
    
    tabButtons.appendChild(detailsTabBtn);
    tabButtons.appendChild(peopleTabBtn);
    tabButtons.appendChild(containedTabBtn);
    tabButtons.appendChild(referencedTabBtn);
    
    // Tab content containers
    const tabContent = document.createElement('div');
    tabContent.className = 'role-panel__tab-content';
    
    // Details tab - contains role name, purpose, accountabilities, responsibilities
    const detailsTab = document.createElement('div');
    detailsTab.className = 'role-panel__tab role-panel__tab--active';
    detailsTab.dataset.tab = 'details';
    
    // Role name (prominent, without label)
    const nameHeading = document.createElement('h2');
    nameHeading.className = 'role-name-heading';
    nameHeading.innerHTML = highlightSearchTerm(node.name || 'Unnamed');
    detailsTab.appendChild(nameHeading);
    
    // Purpose
    const purposeValue = document.createElement('div');
    purposeValue.className = 'value';
    const purposeLabel = document.createElement('div');
    purposeLabel.className = 'label';
    purposeLabel.textContent = t('panel.details.purpose');
    const purposeP = document.createElement('p');
    if (node.purpose) {
        purposeP.innerHTML = renderRoleReferences(node.purpose, roleMap);
    } else {
        const em = document.createElement('em');
        em.className = 'panel-empty-state';
        em.textContent = t('panel.details.noPurpose');
        purposeP.appendChild(em);
    }
    purposeValue.appendChild(purposeLabel);
    purposeValue.appendChild(purposeP);
    detailsTab.appendChild(purposeValue);
    
    // Accountabilities
    const accountabilities = node.accountabilities || [];
    const accountabilitiesValue = document.createElement('div');
    accountabilitiesValue.className = 'value';
    const accountabilitiesLabel = document.createElement('div');
    accountabilitiesLabel.className = 'label';
    accountabilitiesLabel.textContent = t('panel.details.accountabilities');
    accountabilitiesValue.appendChild(accountabilitiesLabel);
    
    if (accountabilities.length > 0) {
        const ul = document.createElement('ul');
        ul.className = 'readonly-list';
        accountabilities.forEach(item => {
            const li = document.createElement('li');
            li.innerHTML = renderRoleReferences(item, roleMap);
            ul.appendChild(li);
        });
        accountabilitiesValue.appendChild(ul);
    } else {
        const p = document.createElement('p');
        const em = document.createElement('em');
        em.className = 'panel-empty-state';
        em.textContent = t('panel.details.noAccountabilities');
        p.appendChild(em);
        accountabilitiesValue.appendChild(p);
    }
    detailsTab.appendChild(accountabilitiesValue);
    
    // Responsibilities
    const responsibilities = node.responsibilities || [];
    const responsibilitiesValue = document.createElement('div');
    responsibilitiesValue.className = 'value';
    const responsibilitiesLabel = document.createElement('div');
    responsibilitiesLabel.className = 'label';
    responsibilitiesLabel.textContent = t('panel.details.responsibilities');
    responsibilitiesValue.appendChild(responsibilitiesLabel);
    
    if (responsibilities.length > 0) {
        const ul = document.createElement('ul');
        ul.className = 'readonly-list';
        responsibilities.forEach(item => {
            const li = document.createElement('li');
            li.innerHTML = renderRoleReferences(item, roleMap);
            ul.appendChild(li);
        });
        responsibilitiesValue.appendChild(ul);
    } else {
        const p = document.createElement('p');
        const em = document.createElement('em');
        em.className = 'panel-empty-state';
        em.textContent = t('panel.details.noResponsibilities');
        p.appendChild(em);
        responsibilitiesValue.appendChild(p);
    }
    detailsTab.appendChild(responsibilitiesValue);
    
    // People tab
    const peopleTab = document.createElement('div');
    peopleTab.className = 'role-panel__tab';
    peopleTab.dataset.tab = 'people';
    const usersLabel = document.createElement('div');
    usersLabel.className = 'label';
    usersLabel.textContent = t('panel.people.label');
    const usersList = document.createElement('div');
    usersList.id = 'role-users-list';
    usersList.className = 'role-users-list';
    const loadingP = document.createElement('p');
    loadingP.className = 'loading-text';
    loadingP.textContent = t('common.loading');
    usersList.appendChild(loadingP);
    peopleTab.appendChild(usersLabel);
    peopleTab.appendChild(usersList);
    
    // Contained roles tab
    const containedTab = document.createElement('div');
    containedTab.className = 'role-panel__tab';
    containedTab.dataset.tab = 'contained';
    const descendants = node.children ? node.children.map((child: any) => ({ id: child.id, name: child.name })) : [];
    containedTab.appendChild(buildContainedRolesSection(descendants));
    
    // Referenced by tab
    const referencedTab = document.createElement('div');
    referencedTab.className = 'role-panel__tab';
    referencedTab.dataset.tab = 'referenced';
    const referencedLabel = document.createElement('div');
    referencedLabel.className = 'label';
    referencedLabel.textContent = t('panel.referenced.label');
    const backlinksList = document.createElement('div');
    backlinksList.id = 'backlinks-list';
    const backlinkLoadingP = document.createElement('p');
    backlinkLoadingP.className = 'loading-text';
    backlinkLoadingP.textContent = t('common.loading');
    backlinksList.appendChild(backlinkLoadingP);
    referencedTab.appendChild(referencedLabel);
    referencedTab.appendChild(backlinksList);
    
    tabContent.appendChild(detailsTab);
    tabContent.appendChild(peopleTab);
    tabContent.appendChild(containedTab);
    tabContent.appendChild(referencedTab);
    
    tabsContainer.appendChild(tabButtons);
    tabsContainer.appendChild(tabContent);
    container.appendChild(tabsContainer);
    
    // Store tab button references for later access
    (container as any).panelTabButtons = { detailsTabBtn, peopleTabBtn, containedTabBtn, referencedTabBtn };
    
    return container;
}

/**
 * Build list section (accountabilities or responsibilities)
 */
export function buildListSection(label: string, items: string[], type: 'accountabilities' | 'responsibilities', inputId: string): HTMLElement {
    const valueDiv = document.createElement('div');
    valueDiv.className = 'value';
    
    const labelDiv = document.createElement('div');
    labelDiv.className = 'label';
    labelDiv.textContent = label;
    
    const listItemsDiv = document.createElement('div');
    listItemsDiv.className = 'list-items';
    listItemsDiv.id = `${type}-list`;
    
    // Items will be added by rerenderList function
    
    const addItemForm = document.createElement('div');
    addItemForm.className = 'add-item-form';
    
    const textarea = document.createElement('textarea');
    textarea.id = inputId;
    textarea.className = 'new-item-textarea';
    textarea.placeholder = `Add ${type === 'accountabilities' ? 'accountability' : 'responsibility'}...`;
    textarea.rows = 2;
    
    const addBtn = document.createElement('button');
    addBtn.className = 'add-item-btn';
    addBtn.dataset.type = type;
    addBtn.textContent = '+';
    
    addItemForm.appendChild(textarea);
    addItemForm.appendChild(addBtn);
    
    valueDiv.appendChild(labelDiv);
    valueDiv.appendChild(listItemsDiv);
    valueDiv.appendChild(addItemForm);
    
    return valueDiv;
}

/**
 * Build purpose field (editable)
 */
export function buildPurposeField(purpose: string, nodeName: string): HTMLElement {
    const valueDiv = document.createElement('div');
    valueDiv.className = 'value';
    
    const labelDiv = document.createElement('div');
    labelDiv.className = 'label';
    labelDiv.textContent = t('panel.details.purpose');
    
    const editableFieldDiv = document.createElement('div');
    editableFieldDiv.className = 'editable-field';
    
    const textarea = document.createElement('textarea');
    textarea.id = 'edit-purpose';
    textarea.dataset.node = nodeName;
    textarea.dataset.field = 'purpose';
    textarea.rows = 3;
    textarea.textContent = purpose;
    textarea.setAttribute('aria-label', 'Role purpose');
    
    const saveHint = document.createElement('span');
    saveHint.className = 'save-hint';
    saveHint.textContent = t('panel.edit.saveHint');
    
    editableFieldDiv.appendChild(textarea);
    editableFieldDiv.appendChild(saveHint);
    valueDiv.appendChild(labelDiv);
    valueDiv.appendChild(editableFieldDiv);
    
    return valueDiv;
}

/**
 * Build role details section (editable view)
 */
export function buildRoleDetailsEdit(node: any): HTMLElement {
    const container = document.createElement('div');
    
    const viewModeLink = buildViewModeLink();
    container.appendChild(viewModeLink);
    
    const name = node.name || 'Unnamed';
    const value = (typeof node.value === 'number' && node.value > 0) ? node.value : 100;
    const color = node.color || '#ffffff';
    const purpose = node.purpose || '';
    const accountabilities = node.accountabilities || [];
    const responsibilities = node.responsibilities || [];
    const children = node.children ? node.children.length : 0;
    
    container.appendChild(buildEditableField('Role', name, name, 'name'));
    container.appendChild(buildPurposeField(purpose, name));
    container.appendChild(buildListSection('Accountabilities', accountabilities, 'accountabilities', 'new-accountability'));
    container.appendChild(buildListSection('Responsibilities', responsibilities, 'responsibilities', 'new-responsibility'));
    
    const descendants = node.children ? node.children.map((child: any) => ({ id: child.id, name: child.name })) : [];
    container.appendChild(buildContainedRolesSection(descendants));
    
    container.appendChild(buildValueField(name, value));
    container.appendChild(buildEditableField('Color', color, name, 'color'));
    
    const childCountValue = document.createElement('div');
    childCountValue.className = 'value';
    const childCountLabel = document.createElement('div');
    childCountLabel.className = 'label';
    childCountLabel.textContent = t('panel.contained.children');
    const childCountP = document.createElement('p');
    childCountP.textContent = String(children);
    childCountValue.appendChild(childCountLabel);
    childCountValue.appendChild(childCountP);
    container.appendChild(childCountValue);
    
    return container;
}

/**
 * Build contained roles section (descendants list)
 */
export function buildContainedRolesSection(descendants: { id: number; name: string }[]): DocumentFragment {
    const fragment = new DocumentFragment();

    const label = document.createElement('div');
    label.className = 'label';
    label.textContent = t('panel.contained.label');
    fragment.appendChild(label);

    if (!descendants || descendants.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'contained-roles-empty panel-empty-state';
        empty.textContent = t('panel.contained.none');
        fragment.appendChild(empty);
        return fragment;
    }

    const list = document.createElement('ul');
    list.className = 'contained-roles-list';

    descendants.forEach(desc => {
        if (!desc?.id || !desc.name) return;
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = '#';
        link.className = 'role-reference role-link';
        link.dataset.roleId = String(desc.id);
        link.textContent = desc.name;
        li.appendChild(link);
        list.appendChild(li);
    });

    fragment.appendChild(list);
    return fragment;
}
